import { useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetSimplePositionDrawings } from 'src/api/position-drawing'
import { useGetProjectPositions } from 'src/api/projectPosition'
import { CenteredSpinner } from 'src/components/Spinner'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { IPositionType } from 'src/generated-types'
import { ConfigureView, DrawingGroupRow, PositionLookups, ZoomRequest } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  buildConfigById,
  buildConfigurationCards,
  buildSuggestedGroupName,
  buildTypeTabs,
  findCardKeyForPosition,
  getNextZoom,
} from './ConfigurePositionsForm.action'
import { EmptyStateFrame, FormRoot, WorkspaceGrid } from './ConfigurePositionsForm.style'
import ConfigurePositionsDrawing from './configure/ConfigurePositionsDrawing'
import ConfigurePositionsPanel from './configure/ConfigurePositionsPanel'
import PositionTypeTabs from './configure/PositionTypeTabs'
import { CONFIGURE_TYPE_ORDER, typeLabelKey } from './configure/configurePositions.action'
import { buildSelectedPositionLabels, comparePositions } from './configure/positionLabels'
import { useConfigureAssignment } from './useConfigureAssignment'
import { useConfigureSelection } from './useConfigureSelection'
import { countChangedByType } from '../../versions/projectVersionChanges.action'
import RevisionModeWorkspace from '../../versions/RevisionModeWorkspace'
import RevisionSelectionPanel from '../../versions/RevisionSelectionPanel'
import { RevisionConfigure, useRevisionToolType } from '../../versions/useRevisionConfigure'

interface ConfigurePositionsFormProps {
  customerId: EntityId
  projectId: EntityId
  localityId: EntityId
  projectForm?: UseFormReturn<any>
  unassignedGroups: DrawingGroupRow[]
  assignedGroups: DrawingGroupRow[]
  lookups: PositionLookups
  fill?: boolean
  // Set while a draft revision is open.
  revision?: RevisionConfigure
}

const ConfigurePositionsForm: React.FC<ConfigurePositionsFormProps> = ({
  customerId,
  projectId,
  localityId,
  projectForm,
  unassignedGroups,
  assignedGroups,
  lookups,
  fill,
  revision,
}) => {
  const { t } = useTranslation('position')
  const theme = useTheme()
  const { data: positions, isLoading } = useGetProjectPositions(customerId, projectId)
  const [activeType, setActiveType] = useState<IPositionType | null>(null)
  const [view, setView] = useState<ConfigureView>('drawing')
  const [zoom, setZoom] = useState(1)
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null)
  const { data: drawings, isLoading: drawingsLoading } = useGetSimplePositionDrawings(
    undefined,
    activeType ?? undefined,
    customerId as number,
  )
  const configuredColor = getThemeColor(theme, MUIThemeColors.successMain)
  const labelFor = useCallback((type: IPositionType) => t(typeLabelKey(type)), [t])

  const visiblePositions = useMemo(() => (positions ?? []).filter(position => !position.isHidden), [positions])
  const configurablePositions = useMemo(
    () => visiblePositions.filter(position => CONFIGURE_TYPE_ORDER.includes(position.type)),
    [visiblePositions],
  )
  const cages = useMemo(
    () => visiblePositions.filter(position => position.type === IPositionType.Cage),
    [visiblePositions],
  )
  const availableTypes = useMemo(
    () => CONFIGURE_TYPE_ORDER.filter(type => configurablePositions.some(position => position.type === type)),
    [configurablePositions],
  )
  const positionById = useMemo(() => new Map((positions ?? []).map(position => [position.id, position])), [positions])
  const positionsOfType = useMemo(
    () => configurablePositions.filter(position => position.type === activeType).sort(comparePositions),
    [configurablePositions, activeType],
  )
  const configById = useMemo(
    () =>
      buildConfigById({
        groupRows: [...assignedGroups, ...unassignedGroups],
        configurablePositions,
        drawingIdToNumber: lookups.drawingIdToNumber,
        configuredColor,
      }),
    [assignedGroups, unassignedGroups, configurablePositions, lookups.drawingIdToNumber, configuredColor],
  )
  const cards = useMemo(() => buildConfigurationCards(positionsOfType, configById), [positionsOfType, configById])
  const highlightedIds = useMemo(
    () => new Set(cards.find(card => card.key === highlightedKey)?.positions.map(position => position.id)),
    [cards, highlightedKey],
  )

  const changedByType = useMemo(() => revision && countChangedByType(revision, positions ?? []), [revision, positions])

  const selection = useConfigureSelection({ activeType, positionsOfType, configById })
  const assignment = useConfigureAssignment({
    customerId,
    projectId,
    localityId,
    activeType,
    cards,
    configById,
    selectedIds: selection.selectedIds,
    drawings,
    setTypeSelection: selection.setTypeSelection,
    removeFromSelection: selection.removeFromSelection,
  })
  const selectedLabels = useMemo(
    () => buildSelectedPositionLabels(selection.selectedIds, positionById, configById),
    [selection.selectedIds, positionById, configById],
  )
  const suggestedGroupName = activeType == null ? '' : buildSuggestedGroupName(labelFor(activeType), selectedLabels)

  useEffect(() => {
    if (activeType != null && availableTypes.includes(activeType)) return
    if (availableTypes[0] != null) setActiveType(availableTypes[0])
  }, [activeType, availableTypes])

  useEffect(() => {
    if (projectForm) projectForm.setValue('positions', positions)
  }, [projectForm, positions])

  const { resetAssignment } = assignment
  const switchType = useCallback(
    (type: IPositionType) => {
      if (type === activeType) return
      setActiveType(type)
      resetAssignment()
    },
    [activeType, resetAssignment],
  )
  useRevisionToolType(revision, activeType, availableTypes, switchType)

  const { togglePosition } = selection
  const onPositionClick = useCallback(
    (positionId: number) => {
      const position = positionById.get(positionId)
      if (!position || activeType == null) return
      if (position.type !== activeType) {
        switchType(position.type)
        toast.info(t('configure-switched', { type: labelFor(position.type) }))
      }
      togglePosition(positionId, position.type)
    },
    [positionById, activeType, switchType, togglePosition, t, labelFor],
  )

  const highlightByPosition = useCallback(
    (positionId: number | null) => setHighlightedKey(findCardKeyForPosition(cards, positionId)),
    [cards],
  )
  const onZoom = useCallback((request: ZoomRequest) => setZoom(current => getNextZoom(current, request)), [])

  if (isLoading) return <CenteredSpinner />

  if (!availableTypes.length || activeType == null) {
    return (
      <EmptyStateFrame>
        <EmptyStatusCard
          imageWidth="240px"
          height="250px"
          contentFontSize="14px"
          headerFontSize="18px"
          content={t('select-type-to-get-position')}
          header={t('no-positions-found')}
        />
      </EmptyStateFrame>
    )
  }

  return (
    <FormRoot fill={fill}>
      <PositionTypeTabs
        tabs={buildTypeTabs(availableTypes, configurablePositions, configById, selection.selectedByType, labelFor).map(
          tab => ({ ...tab, changedCount: changedByType?.get(tab.type) }),
        )}
        activeType={activeType}
        onSelect={switchType}
        disabled={revision != null && revision.mode !== 'configure'}
      />
      <WorkspaceGrid fill={fill}>
        {revision && revision.mode !== 'configure' ? (
          <RevisionModeWorkspace revision={revision} />
        ) : (
          <>
            <ConfigurePositionsDrawing
              customerId={customerId}
              localityId={localityId}
              projectId={projectId}
              view={view}
              zoom={zoom}
              onView={setView}
              onZoom={onZoom}
              activeType={activeType}
              typeLabel={labelFor(activeType)}
              positions={positionsOfType}
              allPositions={configurablePositions}
              cages={cages}
              configById={configById}
              cards={cards}
              highlightedIds={highlightedIds}
              onHoverPosition={highlightByPosition}
              selectedIds={selection.selectedIds}
              onPositionClick={onPositionClick}
              onAdd={selection.addToSelection}
              onRemove={selection.removeFromSelection}
              onToggleGroup={selection.toggleGroup}
              onQuickSelect={selection.quickSelect}
              revision={revision}
            />
            <ConfigurePositionsPanel
              key={activeType}
              cards={cards}
              highlightedKey={highlightedKey}
              onHighlight={setHighlightedKey}
              drawings={drawings ?? []}
              drawingsLoading={drawingsLoading}
              selectedLabels={selectedLabels}
              pickedDrawingId={assignment.pickedDrawingId}
              grouping={assignment.grouping}
              groupName={assignment.groupName}
              saving={assignment.isSaving}
              renameOnly={assignment.isRenameOnly}
              editing={assignment.isEditing}
              existingGroups={assignment.existingGroups}
              lockedGroup={assignment.lockedGroup}
              suggestedGroupName={suggestedGroupName}
              showBridleLegFilter={activeType === IPositionType.Bridle}
              onClear={() => selection.quickSelect('none')}
              onUnselect={id => selection.removeFromSelection([id])}
              onPick={assignment.pickDrawing}
              onGrouping={assignment.changeGrouping}
              onGroupName={assignment.changeGroupName}
              onAssign={assignment.assign}
              onRemoveSelected={() => assignment.confirmRemoveDrawings([...selection.selectedIds])}
              onEditCard={assignment.editCard}
              onRemoveCard={card => assignment.confirmRemoveDrawings(card.positions.map(position => position.id))}
              revisionSection={
                revision && (
                  <RevisionSelectionPanel
                    revision={revision}
                    customerId={customerId}
                    localityId={localityId}
                    projectId={projectId}
                    activeType={activeType}
                    selectedIds={selection.selectedIds}
                    positionById={positionById}
                    onClear={() => selection.quickSelect('none')}
                  />
                )
              }
            />
          </>
        )}
      </WorkspaceGrid>
    </FormRoot>
  )
}

export default ConfigurePositionsForm
